import { ActionIcon, Button, Modal, ModalProps } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import clsx from 'clsx';
import { ListFilter } from 'lucide-react';
import { ComponentPropsWithoutRef, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Breakpoint } from 'shared/config';

export type RegistryFiltersClassNames = Partial<Record<'root' | 'button' | 'inner', string>>;

export type RegistryFiltersProps = {
	visibleFrom?: Breakpoint | null;
	classNames?: RegistryFiltersClassNames;
	modalProps?: Omit<ModalProps, 'opened' | 'onClose'>;
} & ComponentPropsWithoutRef<'div'>;

export const createRegistryFilters = () => {
	const RegistryFilters = forwardRef<HTMLDivElement, RegistryFiltersProps>(
		({ className, classNames, visibleFrom = 'sm', modalProps, children, ...props }, ref) => {
			const { t } = useTranslation();

			const isHidden = visibleFrom !== null;

			const [opened, { close, open }] = useDisclosure();

			return (
				<>
					<div
						ref={ref}
						className={clsx('registry-filters', classNames?.root, className)}
						{...props}
					>
						{isHidden && (
							<ActionIcon
								data-test-id='registry-filters-button'
								variant='outline'
								className={clsx(classNames?.button, {
									[`mantine-hidden-from-${visibleFrom}`]: visibleFrom,
								})}
								onClick={open}
							>
								<ListFilter />
							</ActionIcon>
						)}
						<div
							className={clsx('registry-filters-inner', classNames?.inner, {
								[`mantine-visible-from-${visibleFrom}`]: visibleFrom,
							})}
						>
							{children}
						</div>
					</div>
					{isHidden && (
						<Modal
							ref={ref}
							{...props}
							opened={opened}
							title={t('registry.filters')}
							className={clsx('registry-filters-modal-root', modalProps?.className, {
								[`mantine-hidden-from-${visibleFrom}`]: visibleFrom,
							})}
							closeButtonProps={{ 'data-test-id': 'registry-filters-modal-close' }}
							onClose={close}
							{...modalProps}
						>
							<div className={clsx('registry-filters-modal-inner')}>{children}</div>
							<Button
								data-test-id='registry-filters-modal-close'
								variant='outline'
								className='registry-filters-modal-close'
								onClick={close}
							>
								{t('registry.close')}
							</Button>
						</Modal>
					)}
				</>
			);
		},
	);

	return RegistryFilters;
};
